<template>
  <article class="match-card">
    <header class="match-card__header">
      <div>
        <h4>{{ getUserName(match.user) }}</h4>
        <p class="match-card__meta">
          <span v-if="match.user?.email">{{ match.user.email }}</span>
          <span v-if="showTitle && match.user?.title"> &middot; {{ match.user.title }}</span>
        </p>
      </div>
      <div class="match-card__score">
        <span>Score</span>
        <strong>{{ formatScore(match.score) }}</strong>
      </div>
    </header>

    <template v-if="isAspirational">
      <p v-if="match.explanation?.aspirationText" class="match-card__summary">
        "{{ match.explanation.aspirationText }}"
      </p>

      <div v-if="match.explanation?.desiredArchetypes?.length" class="match-card__list">
        <h5>Desired Archetypes</h5>
        <ul>
          <li
            v-for="(archetype, index) in match.explanation.desiredArchetypes"
            :key="getArchetypeKey(archetype, index)"
            class="match-card__archetype"
          >
            <div class="match-card__archetype-header">
              <div>
                <strong>{{ getArchetypeName(archetype) }}</strong>
                <p v-if="getArchetypeId(archetype)" class="match-card__archetype-id">
                  {{ getArchetypeId(archetype) }}
                </p>
              </div>
              <span class="match-card__badge">
                Score {{ formatScore(getArchetypeScore(archetype)) }}
              </span>
            </div>
            <p v-if="getArchetypeSummary(archetype)" class="match-card__archetype-summary">
              {{ getArchetypeSummary(archetype) }}
            </p>
          </li>
        </ul>
      </div>
    </template>

    <template v-else>
      <p v-if="match.explanation?.resumeSummary" class="match-card__summary">
        {{ match.explanation.resumeSummary }}
      </p>

      <div v-if="match.explanation?.highlightedSkills?.length" class="match-card__chips">
        <span class="chip chip--skill" v-for="skill in match.explanation.highlightedSkills" :key="skill">
          {{ skill }}
        </span>
      </div>

      <div v-if="match.explanation?.highlightedAccomplishments?.length" class="match-card__list">
        <h5>Highlighted Accomplishments</h5>
        <ul>
          <li v-for="accomplishment in match.explanation.highlightedAccomplishments" :key="accomplishment">
            {{ accomplishment }}
          </li>
        </ul>
      </div>

      <div v-if="match.explanation?.certifications?.length" class="match-card__list">
        <h5>Certifications</h5>
        <ul>
          <li v-for="certification in match.explanation.certifications" :key="certification">
            {{ certification }}
          </li>
        </ul>
      </div>

      <div v-if="match.explanation?.recentRoles?.length" class="match-card__list">
        <h5>Recent Roles</h5>
        <ul>
          <li
            v-for="role in match.explanation.recentRoles"
            :key="`${role.company}-${role.position}`"
          >
            <strong>{{ role.position }}</strong> at {{ role.company }} - {{ role.summary }}
          </li>
        </ul>
      </div>

      <p v-if="match.user?.guidance?.generalGuidance" class="match-card__guidance">
        {{ match.user.guidance.generalGuidance }}
      </p>
    </template>
  </article>
</template>

<script>
export default {
  name: 'SalesMatchCard',
  props: {
    match: {
      type: Object,
      required: true,
    },
    variant: {
      type: String,
      default: 'current',
    },
  },
  computed: {
    isAspirational() {
      return this.variant === 'aspirational'
    },
    showTitle() {
      return !this.isAspirational
    },
  },
  methods: {
    getUserName(user) {
      if (!user) {
        return 'Unknown Wrkr'
      }
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
      return name || user.email || 'Unknown Wrkr'
    },
    formatScore(score) {
      if (typeof score === 'number') {
        return score.toFixed(2)
      }
      return 'N/A'
    },
    getArchetypeValue(archetype, key) {
      if (!archetype || !key) {
        return null
      }

      if (Object.prototype.hasOwnProperty.call(archetype, key)) {
        return archetype[key]
      }

      if (archetype._doc && Object.prototype.hasOwnProperty.call(archetype._doc, key)) {
        return archetype._doc[key]
      }

      return null
    },
    getArchetypeName(archetype) {
      return (
        this.getArchetypeValue(archetype, 'archetypeName') ||
        this.getArchetypeValue(archetype, 'name') ||
        this.getArchetypeId(archetype) ||
        'Unknown archetype'
      )
    },
    getArchetypeId(archetype) {
      return this.getArchetypeValue(archetype, 'id')
    },
    getArchetypeScore(archetype) {
      return this.getArchetypeValue(archetype, 'score')
    },
    getArchetypeSummary(archetype) {
      return (
        this.getArchetypeValue(archetype, 'archetypeSummary') ||
        this.getArchetypeValue(archetype, 'summary')
      )
    },
    getArchetypeKey(archetype, index) {
      return this.getArchetypeId(archetype) || index
    },
  },
}
</script>

<style lang="scss" scoped>
.match-card {
  @include card($color-surface, $color-border, $shadow-xs);
  padding: $space-5;
  margin-bottom: $space-4;

  &__header {
    display: flex;
    justify-content: space-between;
    gap: $space-4;
    flex-wrap: wrap;
    margin-bottom: $space-3;
  }

  &__meta {
    color: $color-text-subtle;
    font-size: $font-size-sm;
  }

  &__score {
    text-align: right;
    min-width: 120px;

    span {
      display: block;
      text-transform: uppercase;
      font-size: $font-size-xs;
      color: $color-text-subtle;
    }

    strong {
      font-size: $font-size-xl;
      color: $color-text;
    }
  }

  &__summary {
    margin-bottom: $space-3;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__list {
    margin-bottom: $space-3;

    h5 {
      font-size: $font-size-sm;
      font-weight: 600;
      margin-bottom: $space-1;
    }

    ul {
      padding-left: 1.25rem;

      li {
        margin-bottom: $space-1;
      }
    }
  }

  &__guidance {
    background: $color-surface-soft;
    border-left: 3px solid $color-info;
    padding: $space-3;
    border-radius: $radius-sm;
    color: $color-text;
  }

  &__archetype {
    padding: $space-2 0;
    border-bottom: 1px solid $color-border-muted;

    &:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }
  }

  &__archetype-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: $space-2;
  }

  &__archetype-id {
    font-size: $font-size-xs;
    color: $color-text-subtle;
    margin: $space-1 0 0;
  }

  &__archetype-summary {
    margin: $space-2 0 0;
    color: $color-text-muted;
  }

  &__badge {
    background: $color-surface-muted;
    color: $color-text;
    font-size: $font-size-xs;
    font-weight: 600;
    padding: $space-1 $space-2;
    border-radius: $radius-pill;
  }
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: $space-1 $space-3;
  border-radius: $radius-pill;
  font-size: $font-size-sm;

  &--skill {
    background: $color-surface-muted;
    color: $color-text;
  }
}
</style>
